"use client";

import React, {
  useEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
  useRef,
} from "react";
import { useEditor, EditorContent, Editor, FloatingMenu } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Color } from "@tiptap/extension-color";
import ListItem from "@tiptap/extension-list-item";
import TextStyle from "@tiptap/extension-text-style";
import Placeholder from "@tiptap/extension-placeholder";
import Image from "@tiptap/extension-image";
import { Button } from "@/components/ui/button";
import {
  BoldIcon,
  CodeIcon,
  CodeXml,
  ItalicIcon,
  ListIcon,
  ListOrderedIcon,
  QuoteIcon,
  StrikethroughIcon,
  ImageIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useImageUploader } from "@/components/AppHeader/FileUpload";

import { Extension } from "@tiptap/core";
import {
  Plugin as ProseMirrorPlugin,
  PluginKey as ProseMirrorPluginKey,
} from "@tiptap/pm/state";
import { ReplaceStep } from "@tiptap/pm/transform";
import { UploadedFile } from "@/utils/types";

export interface SyncedEditorProps {
  valueInSupabase: string;
  onUpdateValueInSupabase: (newValue: string) => void;
  classnames?: string;
  placeholder: string;
}

// add break after inserting image, because by default it just gets overwritten by typing anything
const AddBreakAfterImageExtension = Extension.create({
  name: "addBreakAfterImage",

  addProseMirrorPlugins() {
    return [
      new ProseMirrorPlugin({
        key: new ProseMirrorPluginKey("addBreakAfterImage"),
        appendTransaction: (transactions, oldState, newState) => {
          const tr = newState.tr;
          let modified = false;
          const hardBreakNodeType = newState.schema.nodes.hardBreak;

          if (!hardBreakNodeType) {
            return null;
          }

          transactions.forEach((transaction) => {
            if (!transaction.docChanged) return;

            transaction.steps.forEach((step) => {
              if (!(step instanceof ReplaceStep)) {
                return;
              }

              const replaceStep = step as InstanceType<typeof ReplaceStep>;
              const { from, slice } = replaceStep;

              if (slice && slice.content.size > 0) {
                slice.content.forEach((node, offsetInSlice) => {
                  if (node.type.name === "image") {
                    const imageStartPosInNewState = from + offsetInSlice;
                    const imageEndPosInNewState =
                      imageStartPosInNewState + node.nodeSize;

                    let shouldInsert = true;
                    if (imageEndPosInNewState < newState.doc.content.size) {
                      const nodeAfter = newState.doc.nodeAt(
                        imageEndPosInNewState
                      );
                      if (nodeAfter && nodeAfter.type === hardBreakNodeType) {
                        shouldInsert = false;
                      }
                    }

                    const $imageStart = newState.doc.resolve(
                      imageStartPosInNewState
                    );
                    const parentNode = $imageStart.parent;
                    if (
                      !parentNode
                        .contentMatchAt($imageStart.indexAfter())
                        .matchType(hardBreakNodeType)
                    ) {
                      shouldInsert = false;
                    }

                    if (shouldInsert) {
                      tr.insert(
                        imageEndPosInNewState,
                        hardBreakNodeType.create()
                      );
                      modified = true;
                    }
                  }
                });
              }
            });
          });

          return modified ? tr : null;
        },
      }),
    ];
  },
});

export const SyncedTipTapEditor = forwardRef<any, SyncedEditorProps>(
  (props, ref) => {
    if (typeof window === "undefined") {
      return null;
    }

    const {
      triggerUpload: triggerImageUpload,
      isUploading: isUploadingImage,
      FileUploadComponent,
      uploadFiles,
    } = useImageUploader({
      acceptedMimeTypes: [
        "image/png",
        "image/jpeg",
        "image/jpg",
        "image/gif",
        "image/bmp",
        "image/tiff",
        "image/webp",
        "image/heic",
        "image/heif",
        "image/heic-sequence",
        "image/heif-sequence",
        "image/svg+xml",
      ],
      onUploadSuccess: (uploadedFile: UploadedFile) => {
        if (editor && uploadedFile.url) {
          editor.chain().focus().setImage({ src: uploadedFile.url }).run();
        }
      },
    });

    const isLocallyUpdating = useRef(false);

    const extensions = [
      Color.configure({ types: [TextStyle.name, ListItem.name] }),
      TextStyle,
      StarterKit.configure({
        bulletList: {
          HTMLAttributes: {
            class: "list-disc",
          },
        },
        orderedList: {
          HTMLAttributes: {
            class: "list-decimal",
          },
        },
      }),
      Placeholder.configure({
        placeholder: props.placeholder,
      }),
      Image.configure({
        inline: true,
        allowBase64: false,
      }),
      AddBreakAfterImageExtension,
    ];

    const editor = useEditor({
      extensions,
      content: props.valueInSupabase,
      immediatelyRender: false,
      onUpdate: ({ editor }) => {
        isLocallyUpdating.current = true;
        const html = editor.getHTML();
        props.onUpdateValueInSupabase(html);
      },
      editorProps: {
        handlePaste: (view, event, slice) => {
          const files = Array.from(event.clipboardData?.files || []);
          // Filter for image files (e.g., image/png, image/jpeg, image/gif)
          const imageFiles = files.filter((file) =>
            file.type.startsWith("image/")
          );

          if (imageFiles.length > 0 && uploadFiles) {
            event.preventDefault(); // Prevent default Tiptap/ProseMirror paste handling

            const fileToUploads = imageFiles; // Process the first image file
            uploadFiles(fileToUploads); // Use the uploadFiles function from the hook

            // The onUploadSuccess callback in useImageUploader will handle inserting the image
            // with the URL into the editor.

            return true; // Indicate that we've handled the paste
          }

          // Return false to let Tiptap/ProseMirror handle other paste events
          // (e.g., text, or images copied directly as image data from web/other apps)
          return false;
        },
        handleDrop: (view, event, slice, moved) => {
          if (moved) {
            // If it was a drag inside the editor, let Tiptap handle it
            return false;
          }

          const files = Array.from(event.dataTransfer?.files || []);
          const imageFiles = files.filter((file) =>
            file.type.startsWith("image/")
          );

          if (imageFiles.length > 0 && uploadFiles) {
            event.preventDefault(); // Prevent default browser behavior

            // Upload all dropped image files
            uploadFiles(imageFiles);

            // The onUploadSuccess callback in useImageUploader will handle inserting the images
            return true; // Indicate that we've handled the drop
          }

          // Let Tiptap handle other drop events
          return false;
        },
      },
    });

    const MenuBar = ({ editor }: { editor: Editor }) => {
      if (!editor) {
        return null;
      }

      const menuButtons = [
        {
          isActiveKey: "bold",
          text: <BoldIcon className="w-3 h-3" />,
          onClick: () => editor.chain().focus().toggleBold().run(),
          disabled: !editor.can().chain().focus().toggleBold().run(),
        },
        {
          isActiveKey: "italic",
          text: <ItalicIcon className="w-3 h-3" />,
          onClick: () => editor.chain().focus().toggleItalic().run(),
          disabled: !editor.can().chain().focus().toggleItalic().run(),
        },
        {
          isActiveKey: "strike",
          text: <StrikethroughIcon className="w-3 h-3" />,
          onClick: () => editor.chain().focus().toggleStrike().run(),
          disabled: !editor.can().chain().focus().toggleStrike().run(),
        },
        {
          isActiveKey: "code",
          text: <CodeIcon className="w-3.5 h-3.5" />,
          onClick: () => editor.chain().focus().toggleCode().run(),
          disabled: !editor.can().chain().focus().toggleCode().run(),
        },
        {
          isActive: editor.isActive("heading", { level: 1 }),
          text: "H1",
          onClick: () =>
            editor.chain().focus().toggleHeading({ level: 1 }).run(),
        },
        {
          isActive: editor.isActive("heading", { level: 2 }),
          text: "H2",
          onClick: () =>
            editor.chain().focus().toggleHeading({ level: 2 }).run(),
        },
        {
          isActive: editor.isActive("heading", { level: 3 }),
          text: "H3",
          onClick: () =>
            editor.chain().focus().toggleHeading({ level: 3 }).run(),
        },
        {
          isActiveKey: "bulletList",
          text: <ListIcon className="w-3.5 h-3.5" />,
          onClick: () => editor.chain().focus().toggleBulletList().run(),
        },
        {
          isActiveKey: "orderedList",
          text: <ListOrderedIcon className="w-3.5 h-3.5" />,
          onClick: () => editor.chain().focus().toggleOrderedList().run(),
        },
        {
          isActiveKey: "codeBlock",
          text: <CodeXml className="w-3.5 h-3.5" />,
          onClick: () => editor.chain().focus().toggleCodeBlock().run(),
        },
        {
          isActiveKey: "blockquote",
          text: <QuoteIcon className="w-3.5 h-3.5" />,
          onClick: () => editor.chain().focus().toggleBlockquote().run(),
        },
        {
          text: <ImageIcon className="w-3.5 h-3.5" />,
          onClick: triggerImageUpload,
          disabled: isUploadingImage,
        },
      ];
      return (
        <div className="flex flex-wrap flex-row w-full gap-0.5 p-1 bg-background-secondary rounded-md shadow-xl border">
          {menuButtons.map((button, index) => (
            <Button
              key={index}
              size="xs"
              variant={
                button.isActive ||
                (button.isActiveKey && editor.isActive(button.isActiveKey))
                  ? "primary"
                  : "ghost"
              }
              onClick={button.onClick}
              disabled={button.disabled}
              className="px-2 h-7"
            >
              {button.text}
            </Button>
          ))}
        </div>
      );
    };

    useImperativeHandle(ref, () => ({
      getText: () => editor?.getText() || "",
      focus: () => {
        editor?.commands.focus();
      },
    }));

    useEffect(() => {
      if (!editor) {
        return;
      }

      if (isLocallyUpdating.current) {
        isLocallyUpdating.current = false;
        return;
      }

      const currentHTML = editor.getHTML();
      if (currentHTML !== props.valueInSupabase) {
        editor.commands.setContent(props.valueInSupabase, false);
      }
    }, [props.valueInSupabase, editor]);

    const shouldShowFloatingMenu = useCallback(() => {
      if (!editor) return false;
      return editor.state.selection.content().size > 0;
    }, [editor]);

    if (!editor) {
      return null;
    }

    return (
      <div
        id="tiptap-editor"
        className={cn(
          "flex flex-col overflow-y-scroll hide-scrollbar pb-2 shrink-0 min-h-64",
          props.classnames
        )}
      >
        {editor && (
          <FloatingMenu
            editor={editor}
            shouldShow={shouldShowFloatingMenu}
            tippyOptions={{
              duration: 100,
              placement: "top",
              maxWidth: "none",
            }}
          >
            <MenuBar editor={editor} />
          </FloatingMenu>
        )}
        <FileUploadComponent className="hidden" />
        <EditorContent
          editor={editor}
          autoFocus={true}
          className="focus-visible:outline-none flex grow"
        />
      </div>
    );
  }
);

export default React.memo(SyncedTipTapEditor);
